import L from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";

interface Props {
  position: L.ControlPosition;
  children?: React.ReactNode;
  container?: React.HTMLAttributes<HTMLDivElement>;
  prepend?: boolean;
}

const POSITION_CLASSES = {
  bottomleft: "leaflet-bottom leaflet-left",
  bottomright: "leaflet-bottom leaflet-right",
  topleft: "leaflet-top leaflet-left",
  topright: "leaflet-top leaflet-right",
};

/*
Leafletにカスタムコントロールを追加するためのコンポーネント
参考: https://github.com/chris-m92/react-leaflet-custom-control
 */
export const CustomControl = ({
  position,
  children,
  container,
  prepend = false,
}: Props): React.ReactPortal | null => {
  // Leafletのコントロール領域に差し込むコンテナ
  const controlContainerRef = useRef<HTMLDivElement>(
    document.createElement("div")
  ).current;
  // ポータルのルート要素
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  // Leafletのマップオブジェクト
  const map = useMap();

  // position
  const positionClass = useMemo(
    () => POSITION_CLASSES[position] || POSITION_CLASSES.topright,
    [position]
  );

  // コンテナ上のクリックとスクロールの伝播を無効化
  useEffect(() => {
    L.DomEvent.disableClickPropagation(controlContainerRef);
    L.DomEvent.disableScrollPropagation(controlContainerRef);
  }, [controlContainerRef]);

  // positionの設定
  useEffect(() => {
    const mapContainer = map.getContainer();
    const targetDiv = mapContainer.getElementsByClassName(positionClass)[0];
    if (targetDiv instanceof HTMLElement) {
      setPortalRoot(targetDiv);
    }
  }, [positionClass, map]);

  useEffect(() => {
    if (!portalRoot) return;

    const containerElement = controlContainerRef;
    if (prepend) {
      portalRoot.prepend(containerElement);
    } else {
      portalRoot.append(containerElement);
    }

    return () => {
      if (portalRoot.contains(containerElement)) {
        portalRoot.removeChild(containerElement);
      }
    };
  }, [portalRoot, prepend, controlContainerRef]);

  const className = `${container?.className ?? ""} leaflet-control`.trim();

  useEffect(() => {
    controlContainerRef.className = className;
    if (container?.style) {
      Object.assign(controlContainerRef.style, container.style);
    }
  }, [className, container, controlContainerRef]);

  return portalRoot ? createPortal(<>{children}</>, controlContainerRef) : null;
};
